/**
 * Override the core usePayment hook.
 * Added custom functions and states.
 */
import getSymbolFromCurrency from 'currency-symbol-map';

import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
	deleteAllPaymentInstructionFetcher,
	usePayment as useCorePayment,
} from '@/core/data/Content/Payment';
import { STRING_TRUE } from '@/data/constants/catalog';
import {
	ACCOUNT_CC,
	BLOCKED,
	CC_CVC,
	CREDIT_CARD_FORM_DATA_INIT,
	EXPIRE_MONTH,
	EXPIRE_YEAR,
	IS_PERSONAL_ADDRESS_ALLOWED,
	NON_CREDIT_CARD_PAYMENT_METHODS,
	PAYMENT,
	PAYMENT_CONFIGS,
	PO_NUMBER_STORAGE_PREFIX,
	SHIP_MODE_CODE_PICKUP,
} from '@/data/constants/order';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useCheckOutV2 } from '@/data/Content/CheckOutV2';
import { useChannelId } from '@/data/state/useChannelId';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { ID } from '@/data/types/Basic';
import { Order } from '@/data/types/Order';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { isNumeric } from '@/data/utils/orderUtils';
import { getPaymentToEdit, markSinglePaymentDirtyIfNeeded } from '@/data/utils/payment';
import { checkIsCheckoutDisabled } from '@/data/utils/processOrderData';
import { isAbortNavigation } from '@/utils/errorUtils';
import { CartPaymentInstruction } from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import transactionsPaymentInstruction from 'integration/generated/transactions/transactionsPaymentInstruction';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import {
	ENABLE_AUTO_SELECT_CHECKOUT_PROFILE,
	SELECTED_PROFILE_STORAGE,
} from 'shared/custom/constants/commonConstants';
import { KeyedMutator } from 'swr';

export { getPaymentToEdit, markSinglePaymentDirtyIfNeeded };

export interface CreditCardFormDataType {
	account: string;
	expire_month: string;
	expire_year: string;
	cc_cvc: string;
	cc_brand: string;
}

export type PaymentType =
	(typeof PAYMENT.paymentMethodName)[keyof typeof PAYMENT.paymentMethodName];
export interface PaymentInfoType {
	payMethodId: PaymentType | undefined;
	creditCardFormData: CreditCardFormDataType;
	addressId: string;
	piAmount: string;
	piCurrency: string;
	paymentTermConditionId: string;
	title: string;
	policyId: string;
}

export type NonCreditCardPaymentType = (typeof NON_CREDIT_CARD_PAYMENT_METHODS)[number];
export type CreditCardPaymentType = Exclude<PaymentType, NonCreditCardPaymentType>;

const NON_CC_SET: ReadonlySet<PaymentType> = new Set(NON_CREDIT_CARD_PAYMENT_METHODS);
const BLOCKED_SET: ReadonlySet<PaymentType> = new Set(BLOCKED);

export const isNonCreditCardPaymentMethod = (
	method?: PaymentType
): method is NonCreditCardPaymentType => method !== undefined && NON_CC_SET.has(method);

export const isBlockedPaymentMethod = (method?: PaymentType): method is PaymentType =>
	method !== undefined && BLOCKED_SET.has(method);

/**
 * usePayment hook
 * @override
 */
export const usePayment = (cart: Order, _mutateCart?: KeyedMutator<Order>) => {
	// invoke the OOB functions first to get required states and functions.
	const coreUsePayment = useCorePayment(cart, _mutateCart);
	const { usablePayments } = coreUsePayment;
	const routes = useLocalization('Routes');
	const trackException = useTrackException();
	const checkoutValues = useContext(ContentContext) as ReturnType<typeof useCheckOutV2>;
	const { poRequired: isPONumberRequired, isGuest, submitCheckout } = checkoutValues;
	const { orderItem: orderItems } = cart;

	const isCheckoutDisabled = checkIsCheckoutDisabled(orderItems);

	const router = useNextRouter();
	const { settings } = useSettings();
	const { langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const [createNewAddress, setCreateNewAddress] = useState<boolean>(false);
	const [editAddress, setEditAddress] = useState<boolean>(false);
	const isAutoSelectProfileEnabled =
		(settings?.userData?.[ENABLE_AUTO_SELECT_CHECKOUT_PROFILE] ?? 'true') === 'true';
	const [currentPaymentNumber, setCurrentPaymentNumber] = useState<number>(0);
	const channelId = useChannelId();

	// TODO handle WC_ProCustomerLevel when organization data is available
	// const organization = useOrganization();
	// const proCustomerLevel = organization?.WC_ProCustomerLevel;

	const PAYMENT_INFO_INIT: PaymentInfoType = useMemo(
		() => ({
			payMethodId: undefined,
			creditCardFormData: CREDIT_CARD_FORM_DATA_INIT,
			addressId: '',
			piAmount: cart?.grandTotal ?? '0',
			piCurrency: cart?.grandTotalCurrency ?? '',
			paymentTermConditionId: '',
			policyId: '',
			title: '',
		}),
		[cart?.grandTotal, cart?.grandTotalCurrency]
	);
	const [selectedPaymentInfoList, setSelectedPaymentInfoList] = useState<PaymentInfoType[]>([]);
	const [useMultiplePayment, setUseMultiplePayment] = useState<boolean>(false);
	const [addNewPayment, setAddNewPayment] = useState<boolean>(false);
	const [editPayment, setEditPayment] = useState<boolean>(false);
	const [poNumber, setPONumber] = useState<string>('');
	const [isEditPayment, setIsEditPayment] = useState<boolean>(false);
	const [isAddPayment, setIsAddPayment] = useState<boolean>(false);
	const [isReviewOrderClicked, setIsReviewOrderClicked] = useState<boolean>(false);

	/**
	 * Gets the total piAmount from the specified payment list
	 * @returns Total piAmount
	 */
	const getRunningPaymentTotal = useCallback((paymentList: PaymentInfoType[]) => {
		if (paymentList?.length > 0) {
			return paymentList.reduce((acc, p) => acc + parseFloat(p.piAmount), 0);
		}
		return 0;
	}, []);

	/**
	 * Checks if the list of payment method's piAmounts add up to the Order Total
	 * @returns Whether the total piAmounts equals to the Order Total
	 */
	const isOrderTotalMatched = useCallback(() => {
		if (selectedPaymentInfoList.length > 0 && cart?.grandTotal) {
			return (
				parseFloat(cart.grandTotal).toFixed(2) ===
				parseFloat(getRunningPaymentTotal(selectedPaymentInfoList).toFixed(2)).toFixed(2)
			);
		}
		return false;
	}, [selectedPaymentInfoList, cart?.grandTotal, getRunningPaymentTotal]);

	/**
	 * Checks if more new payments are allowed
	 * @returns Whether additional new payments are allowed
	 */
	const allowMorePayments = useMemo(() => {
		if (selectedPaymentInfoList.length >= PAYMENT_CONFIGS.maxNumPayment) {
			return false;
		}
		return !isOrderTotalMatched();
	}, [selectedPaymentInfoList, isOrderTotalMatched]);

	const handlePONumberChange = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const newPONumber = event.target.value;
			setPONumber(newPONumber);
		},
		[]
	);

	const isPersonalAddressAllowed: string =
		cart && cart[IS_PERSONAL_ADDRESS_ALLOWED] ? cart[IS_PERSONAL_ADDRESS_ALLOWED] : STRING_TRUE;

	const grandTotalforErrorMsg: string = useMemo(
		() =>
			(getSymbolFromCurrency(cart?.grandTotalCurrency ?? '') ?? '') +
			parseFloat(cart?.grandTotal ?? '0'),
		[cart?.grandTotal, cart?.grandTotalCurrency]
	);

	useEffect(() => {
		const mapPaymentInstructionToInfo = (pi: any): PaymentInfoType => {
			const payMethodId = pi.payMethodId;
			const paymentFromList = usablePayments?.find(
				(payment) => payment.xumet_policyId === pi.xpaym_policyId
			);

			const creditCardFormData = { ...CREDIT_CARD_FORM_DATA_INIT };

			if (!NON_CREDIT_CARD_PAYMENT_METHODS.includes(payMethodId)) {
				pi.protocolData?.forEach((pd: any) => {
					if ([ACCOUNT_CC, EXPIRE_MONTH, EXPIRE_YEAR].includes(pd.name)) {
						creditCardFormData[pd.name as keyof CreditCardFormDataType] = pd.value;
					}
				});
			}

			return {
				payMethodId,
				creditCardFormData,
				addressId: pi.billing_address_id,
				piAmount: pi.piAmount,
				piCurrency: pi.piCurrency,
				paymentTermConditionId: paymentFromList?.paymentTermConditionId ?? '',
				policyId: pi.xpaym_policyId,
				title: paymentFromList?.description ?? '',
			};
		};

		if (cart?.paymentInstruction?.length > 0) {
			const savedPaymentInfo = cart.paymentInstruction.map(mapPaymentInstructionToInfo);
			if (cart.paymentInstruction.length === 1) {
				const paymentInfo = savedPaymentInfo[0];
				if (paymentInfo) {
					paymentInfo.piAmount = cart.grandTotal;
					if (paymentInfo.creditCardFormData) {
						paymentInfo.creditCardFormData[ACCOUNT_CC] = '';
					}
				}
				setUseMultiplePayment(false);
			} else {
				setUseMultiplePayment(true);
			}
			setSelectedPaymentInfoList(savedPaymentInfo);
		} else {
			setSelectedPaymentInfoList((current) => {
				if (!current.some(({ payMethodId }) => payMethodId !== undefined)) {
					return [{ ...PAYMENT_INFO_INIT }];
				}
				if (current.length > 1) return current;

				const [selected] = current;
				const { piAmount, piCurrency } = PAYMENT_INFO_INIT;
				return selected.piAmount === piAmount && selected.piCurrency === piCurrency
					? current
					: [{ ...selected, piAmount, piCurrency }];
			});
		}
	}, [cart?.paymentInstruction, cart?.grandTotal, PAYMENT_INFO_INIT]);

	useEffect(() => {
		if (cart && poNumber === '') {
			const poNumberFromStorage = localStorage.getItem(PO_NUMBER_STORAGE_PREFIX + cart.orderId);

			if (poNumberFromStorage !== null && poNumberFromStorage !== '') {
				setPONumber(poNumberFromStorage);
			}
		}
	}, [cart, cart?.orderId, poNumber]);

	/** Handle toggle between single/multiple payments */
	const handleMultiplePaymentChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newUseMultiplePayment = event.target.checked;
			if (newUseMultiplePayment) {
				setSelectedPaymentInfoList([]);
				setCurrentPaymentNumber(-1);
			} else {
				const singlePaymentInit: PaymentInfoType = Object.assign({}, PAYMENT_INFO_INIT);
				setSelectedPaymentInfoList([singlePaymentInit]);
				setCurrentPaymentNumber(0);
			}
			setUseMultiplePayment(newUseMultiplePayment);
		},
		[PAYMENT_INFO_INIT]
	);

	/** Handle adding new payments for multiple payments */
	const handleAddNewPayment = useCallback(
		(newPaymentInfo?: PaymentInfoType, paymentNumber?: number) => {
			if (allowMorePayments) {
				setAddNewPayment(true);
				setCurrentPaymentNumber(selectedPaymentInfoList.length);
				const newPaymentInfoList: PaymentInfoType[] = selectedPaymentInfoList.slice();
				if (newPaymentInfo && paymentNumber && paymentNumber >= 0) {
					newPaymentInfoList.splice(paymentNumber, 1, newPaymentInfo);
				}
				const remainingPi =
					parseFloat(cart?.grandTotal ?? '0') - getRunningPaymentTotal(newPaymentInfoList);
				const newPaymentInit: PaymentInfoType = {
					...PAYMENT_INFO_INIT,
					piAmount: remainingPi.toString(),
				};
				newPaymentInfoList.push(newPaymentInit);
				setSelectedPaymentInfoList(newPaymentInfoList);
				setIsAddPayment(true);
			}
		},
		[
			allowMorePayments,
			selectedPaymentInfoList,
			cart?.grandTotal,
			getRunningPaymentTotal,
			PAYMENT_INFO_INIT,
		]
	);

	/** Handle edit payment for multiple payments */
	const handleEditPayment = useCallback(
		(paymentNumber: number) => {
			setEditPayment(true);
			setCurrentPaymentNumber(paymentNumber);
			const newPaymentInfoList: PaymentInfoType[] = selectedPaymentInfoList.slice();
			const newPaymentInfo = { ...newPaymentInfoList[paymentNumber] };
			if (newPaymentInfo && newPaymentInfo.creditCardFormData) {
				newPaymentInfo.creditCardFormData = {
					...newPaymentInfo.creditCardFormData,
				};
				newPaymentInfo.creditCardFormData[ACCOUNT_CC] = '';
				newPaymentInfo.creditCardFormData[CC_CVC] = '';
				newPaymentInfoList.splice(paymentNumber, 1, newPaymentInfo);
				setSelectedPaymentInfoList(newPaymentInfoList);
			}
			setIsEditPayment(true);
		},
		[selectedPaymentInfoList]
	);

	/** Handle delete payment for multiple payments */
	const handleDeletePayment = useCallback(
		(paymentNumber: number) => {
			setIsEditPayment(true);
			const newPaymentInfoList: PaymentInfoType[] = selectedPaymentInfoList.slice();
			newPaymentInfoList.splice(paymentNumber, 1);
			setSelectedPaymentInfoList(newPaymentInfoList);
		},
		[selectedPaymentInfoList]
	);

	/** Handle cancelling new payments for multiple payments */
	const handleCancelNewPayment = useCallback(() => {
		if (addNewPayment) {
			const newPaymentInfoList: PaymentInfoType[] = selectedPaymentInfoList.slice();
			newPaymentInfoList.pop();
			setSelectedPaymentInfoList(newPaymentInfoList);
			setCurrentPaymentNumber(newPaymentInfoList.length - 1);
		}
		setAddNewPayment(false);
		setEditPayment(false);
	}, [addNewPayment, selectedPaymentInfoList]);

	/**
	 * Update the selectedPaymentInfoList state
	 */
	const updateSelectedPaymentInfoList = useCallback(
		(newPaymentInfo: PaymentInfoType, paymentNumber: number) => {
			setSelectedPaymentInfoList((prevSelectedPaymentInfoList) => {
				const newSelectedPaymentInfoList = prevSelectedPaymentInfoList.slice();
				newSelectedPaymentInfoList.splice(paymentNumber, 1, newPaymentInfo);
				return newSelectedPaymentInfoList;
			});
		},
		[]
	);

	/** Handle saving the new/updated payments for multiple payments */
	const handleSavePayment = useCallback(
		(newPaymentInfo: PaymentInfoType, paymentNumber: number) => {
			setAddNewPayment(false);
			setEditPayment(false);
			if (!addNewPayment && !editPayment && isEditPayment) {
				setIsEditPayment(false);
			}
			updateSelectedPaymentInfoList(newPaymentInfo, paymentNumber);
		},
		[addNewPayment, editPayment, isEditPayment, updateSelectedPaymentInfoList]
	);

	/**
	 * Gets the maximum amount allowed for this payment method based on the running total of other payments   */
	const getMaximumPiAmount = useCallback(
		(paymentNumber: number) => {
			if (selectedPaymentInfoList.length > 1) {
				const otherPayments: PaymentInfoType[] = selectedPaymentInfoList.slice();
				otherPayments.splice(paymentNumber, 1);
				return parseFloat(cart.grandTotal) - getRunningPaymentTotal(otherPayments);
			}
			return parseFloat(cart.grandTotal);
		},
		[selectedPaymentInfoList, cart.grandTotal, getRunningPaymentTotal]
	);

	const handlePiAmountChange = useCallback(
		(valueAsNumber: number, paymentNumber: number) => {
			if (valueAsNumber) {
				const newPaymentInfo: PaymentInfoType = {
					...selectedPaymentInfoList[paymentNumber],
					piAmount: valueAsNumber.toString(),
				};

				const newPaymentInfoList: PaymentInfoType[] = selectedPaymentInfoList.slice();
				newPaymentInfoList.splice(paymentNumber, 1, newPaymentInfo);
				setSelectedPaymentInfoList(newPaymentInfoList);
				setIsEditPayment(true);
			}
		},
		[selectedPaymentInfoList]
	);

	/**
	 * Checks if the entered credit card number is a numeric value
	 * @returns Whether the card number format is valid.
	 */
	const isValidCardNumber = useCallback(
		(account: string) => {
			if (cart?.paymentInstruction?.length > 1 && !isEditPayment) {
				return true;
			}
			return isNumeric(account?.trim());
		},
		[cart?.paymentInstruction?.length, isEditPayment]
	);

	/**
	 * Checks if the entered cvv code entered is a numeric value
	 * @returns Whether the cvv code format is valid.
	 */
	const isValidCode = useCallback(
		(cc_cvc: string) => {
			if (cart?.paymentInstruction?.length > 1 && !isEditPayment) {
				return true;
			}
			return isNumeric(cc_cvc?.trim());
		},
		[cart?.paymentInstruction?.length, isEditPayment]
	);

	/**
	 * Validate billing address id for a payment
	 * @returns Whether or not the billing address is valid
	 */
	const isValidBillingAddress = useCallback(
		(paymentInfo: PaymentInfoType) => {
			if (!isGuest && paymentInfo.addressId === '') {
				const allBopis = orderItems.every((item) => item.shipModeCode === SHIP_MODE_CODE_PICKUP);
				if (allBopis) return true;

				return false;
			} else if (isGuest) {
				return true;
			}

			return true;
		},
		[isGuest, orderItems]
	);

	/**
	 * Validate payment options/credit card for a payment
	 * @returns Whether or not the payment method is valid
	 */
	const isValidPaymentMethod = useCallback(
		(paymentInfo: PaymentInfoType) => {
			if (!paymentInfo.payMethodId) return false;
			if (
				(cart?.paymentInstruction?.length > 1 && !isEditPayment) ||
				paymentInfo.paymentTermConditionId !== ''
			) {
				return true;
			}

			const methodsSkippingValidation: PaymentType[] = [
				...NON_CREDIT_CARD_PAYMENT_METHODS,
				PAYMENT.paymentMethodName.giftCard,
				PAYMENT.paymentMethodName.visa,
			];

			if (!methodsSkippingValidation.includes(paymentInfo.payMethodId)) {
				const { creditCardFormData } = paymentInfo;
				if (Object.values(creditCardFormData).some((val) => val?.trim() === '')) return false;
				if (!isValidCardNumber(creditCardFormData.account)) return false;
				if (!isValidCode(creditCardFormData.cc_cvc)) return false;
			}
			return true;
		},
		[cart?.paymentInstruction?.length, isEditPayment, isValidCardNumber, isValidCode]
	);

	/**
	 * Validate billing address id and payment options/credit card for a payment
	 * @returns Whether or not the payment is valid
	 */
	const isValidPayment = useCallback(
		(paymentInfo: PaymentInfoType) => {
			if (!isValidBillingAddress(paymentInfo)) {
				return false;
			}
			if (!isValidPaymentMethod(paymentInfo)) {
				return false;
			}
			return true;
		},
		[isValidBillingAddress, isValidPaymentMethod]
	);

	/**
	 * Validate whether po number is required and specified
	 * @returns Whether po number is valid or needed
	 */
	const isValidPO = useCallback(() => {
		if (isPONumberRequired) {
			if (poNumber.trim() === '') {
				return false;
			}
		}
		return true;
	}, [isPONumberRequired, poNumber]);

	/**
	 * Validate whether shopper can proceed to next step
	 * @returns Whether next step is allowed
	 */
	const canContinue = useCallback(
		() => !isCheckoutDisabled && isValidPO() && isOrderTotalMatched(),
		[isCheckoutDisabled, isValidPO, isOrderTotalMatched]
	);

	const updatePaymentInstructions = useCallback(async () => {
		if (!settings?.storeId) return;
		if (isEditPayment || isAddPayment || !useMultiplePayment) {
			await deleteAllPaymentInstructionFetcher(true)(settings.storeId, { langId }, params);
		}
		const checkoutProfileSelected = localStorage.getItem(SELECTED_PROFILE_STORAGE);
		const promises = selectedPaymentInfoList.map((payment) => {
			const { payMethodId, piAmount, addressId, paymentTermConditionId, creditCardFormData } =
				payment;
			if (!payMethodId) return;
			const billingAddressId = Array.isArray(addressId) ? addressId[0] : addressId;
			const body = {
				piAmount,
				billing_address_id: billingAddressId,
				payMethodId,
				x_msiValue: '1',
				x_channelId: channelId,
			};
			if (checkoutProfileSelected && isAutoSelectProfileEnabled) {
				Object.assign(body, {
					xchkout_ProfileId: checkoutProfileSelected,
				});
			}
			if (!isNonCreditCardPaymentMethod(payMethodId)) {
				if (paymentTermConditionId === '') {
					Object.assign(body, {
						account: creditCardFormData.account.trim(),
						expire_month: creditCardFormData.expire_month,
						expire_year: creditCardFormData.expire_year,
						cc_cvc: creditCardFormData.cc_cvc.trim(),
						cc_brand: payMethodId,
					});
				} else {
					Object.assign(body, {
						account: '',
						expire_month: '',
						expire_year: '',
						valueFromPaymentTC: STRING_TRUE,
					});
				}
			}
			return addPaymentInstructionFetcher(true)(
				settings.storeId,
				{},
				body as unknown as CartPaymentInstruction,
				params
			);
		});

		await Promise.all(promises);
	}, [
		isAutoSelectProfileEnabled,
		settings?.storeId,
		langId,
		params,
		selectedPaymentInfoList,
		isEditPayment,
		isAddPayment,
		useMultiplePayment,
		channelId,
	]);

	/**
	 * Create new payment instruction
	 * @param pub
	 * @returns
	 */
	const addPaymentInstructionFetcher =
		(pub: boolean) =>
		async (
			storeId: string,
			query: {
				[key: string]: string | boolean | ID[] | number;
			},
			data: CartPaymentInstruction,
			params: RequestParams
		) => {
			const piResponse = await transactionsPaymentInstruction(
				pub
			).paymentInstructionAddPaymentInstruction(storeId, query, data, params);
			return piResponse;
		};

	/**
	 * Submit the selected payment instructions for non-CyberSources cards
	 */
	const submitPayment = useCallback(async () => {
		const payMethodId = selectedPaymentInfoList[0]?.payMethodId;

		if (!payMethodId || isBlockedPaymentMethod(payMethodId)) {
			return;
		}

		if (canContinue() && !isReviewOrderClicked) {
			setIsReviewOrderClicked(true);
			try {
				await updatePaymentInstructions();
				await submitCheckout(payMethodId); // CheckOutV2.ts
				const emailList: string[] = [];
				cart.paymentInstruction
					?.filter(({ email1 }) => email1?.trim())
					.forEach(({ email1 }) => emailList.push(email1));

				await router.push({
					pathname: routes.OrderConfirmation.route.t(),
					query: { orderId: cart?.orderId ?? '' },
				});

				// TODO do we need this?
				// if (isPONumberRequired && cart?.orderId) {
				// 	localStorage.setItem(PO_NUMBER_STORAGE_PREFIX + cart.orderId, poNumber);
				// }

				// TODO: Add analytics tracking
			} catch (error) {
				trackException(error, false, routes.OrderConfirmation.route.t());
				if (isAbortNavigation(error)) return;
				console.error('Error submitting payment:', error);
				throw error;
			} finally {
				setIsReviewOrderClicked(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		canContinue,
		isReviewOrderClicked,
		isPONumberRequired,
		cart?.orderId,
		poNumber,
		isEditPayment,
		isAddPayment,
		useMultiplePayment,
		updatePaymentInstructions,
	]);

	return {
		...coreUsePayment, // export OOB usePayment hook functions and states
		createNewAddress,
		editAddress,
		addNewPayment, // y
		useMultiplePayment, // y
		handleMultiplePaymentChange,
		setCreateNewAddress,
		setEditAddress,
		handleCancelNewPayment,
		currentPaymentNumber,
		isPersonalAddressAllowed,
		isPONumberRequired,
		poNumber,
		handlePONumberChange,
		isValidPO,
		selectedPaymentInfoList,
		isValidCardNumber,
		isValidCode,
		// usablePayments, // OOB provided this
		handleAddNewPayment, // y
		allowMorePayments,
		// back,
		canContinue,
		submitPayment,
		isValidPayment,
		setAddNewPayment,
		grandTotalforErrorMsg,
		handleEditPayment,
		handleDeletePayment,
		handleSavePayment,
		getMaximumPiAmount,
		handlePiAmountChange,
		editPayment, // TODO rename editPayment and isEditPayment to avoid confusion
		updateSelectedPaymentInfoList, // y
		isOrderTotalMatched,
		isEditPayment,
	};
};

export * from '@/core/data/Content/Payment';
